import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSuspenseAPIKeyQuery } from '#api/api-keys.queries.ts';
import { MissingResourceError } from '#api/errors.ts';
import { DeleteIcon } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { fromArray, Titled } from '#util/title.ts';
import { DeleteAPIKeyDialog } from '../delete/DeleteAPIKeyDialog.tsx';
import { APIKeyForm } from '../form/APIKeyForm.tsx';
import { ExpiresAt } from './ExpiresAt.tsx';
import { LastSeenAt } from './LastSeenAt.tsx';
import styles from './APIKeyDetailPage.module.css';

export function APIKeyDetailPage() {
  const { apiKeyID } = useParams() as { apiKeyID: string };
  const {
    data: { data: apiKey },
    error,
    isFetching,
  } = useSuspenseAPIKeyQuery(apiKeyID);
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if this has been deleted (e.g. in a different tab)
  if (error && !isFetching && error instanceof MissingResourceError) {
    throw error;
  }

  return (
    <Titled title={(title) => fromArray([apiKey.name, title])}>
      <MainLayout
        header={
          <PageHeader
            title={
              <>
                <Link to="/settings/api-keys" className="a">
                  API keys
                </Link>
                {' / '}
                <span>{apiKey.name}</span>
              </>
            }
            subtitle={
              <span className={styles.APIKeyDetailPage_subtitle}>
                <span className="mb-8">
                  ID: <CopyPill value={apiKey.id} />
                </span>
                <span className="mb-8">
                  Last used: <LastSeenAt lastSeenAt={apiKey.lastSeenAt} createdAt={apiKey.createdAt} />
                </span>
                <span className="mb-8">
                  <ExpiresAt expiresAt={apiKey.expiresAt} withLabel={true} />
                </span>
              </span>
            }
          >
            <ButtonGroup>
              <ButtonGroupItem>
                <Tooltip
                  content="Delete"
                  render={<Button size="small" variant="icon" onClick={() => setIsDeleting(true)} />}
                >
                  <DeleteIcon aria-hidden="true" />
                  <span className="visually-hidden">Delete</span>
                </Tooltip>
              </ButtonGroupItem>
            </ButtonGroup>
          </PageHeader>
        }
      >
        <APIKeyForm
          id={apiKey.id}
          onSuccess={() => void navigate('/settings/api-keys')}
          defaultValues={{ name: apiKey.name, permissions: apiKey.permissions ?? [], roleID: apiKey.roleID }}
        />
        {isDeleting ?
          <DeleteAPIKeyDialog
            apiKeyID={apiKeyID}
            onDelete={() => {
              void navigate('/settings/api-keys', { replace: true });
            }}
            onCancel={() => {
              setIsDeleting(false);
            }}
          />
        : null}
      </MainLayout>
    </Titled>
  );
}
